import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, EyeOff, Timer, BadgePercent, UserCheck, LayoutDashboard, Zap } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";

const OFFERS = [
  { icon: EyeOff, t: "White-label planning", d: "No OrthoPlaniX branding in any doctor-facing communication. Plans are delivered under your lab's identity." },
  { icon: Timer, t: "SLA-backed turnaround", d: "Defined delivery commitments per case type, agreed contractually — so your production schedule never waits on planning." },
  { icon: BadgePercent, t: "Volume pricing", d: "Tiered per-case rates that improve with monthly volume. Predictable planning cost per unit." },
  { icon: UserCheck, t: "Dedicated account manager", d: "A single point of contact who knows your doctors, your conventions and your escalation paths." },
  { icon: LayoutDashboard, t: "Case dashboard", d: "Every case tracked live — status, revisions, approvals and history across all the doctors you serve." },
  { icon: Zap, t: "Priority queue", d: "Lab partner cases enter a priority planning lane with faster standard turnaround." },
];

const FLOW = ["Dental Lab", "OrthoPlaniX plans", "Dentist reviews & approves", "Your lab manufactures"];

export default function ForLabs() {
  return (
    <MarketingLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <section className="pt-40 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>For Dental Labs</SectionTag>
            <h1 className="font-display font-black tracking-tight leading-[0.98] text-[clamp(2.6rem,6.5vw,5.5rem)] max-w-4xl">
              <LineMask delay={0.1}>Your lab manufactures.</LineMask>
              <LineMask delay={0.25} className="text-neon">We plan.</LineMask>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-8 max-w-2xl text-base md:text-lg text-slate-400 leading-relaxed">
                Add professional clear-aligner treatment planning to your workflow without building an internal planning team. You keep the doctor relationship and the manufacturing. OrthoPlaniX becomes your invisible planning department.
              </p>
            </FadeIn>
            <FadeIn delay={0.7}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="labs-talk-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,229,255,0.45)]">
                  Talk to the lab team <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/enterprise" data-testid="labs-enterprise-link"
                  className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-neon hover:text-neon">
                  Enterprise planning <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>

        <section className="py-16 border-y border-white/10 bg-ink-2/40" data-testid="labs-flow">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid sm:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
              {FLOW.map((f, i) => (
                <FadeIn key={f} delay={i * 0.08} className="h-full">
                  <div className="h-full bg-ink p-6 lg:p-8">
                    <p className="font-display font-black text-4xl text-neon/25">{String(i + 1).padStart(2, "0")}</p>
                    <p className="font-display font-bold text-lg mt-4">{f}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
            <FadeIn>
              <p className="mt-6 text-xs text-slate-500 text-center">The doctor relationship stays yours at every step. We plan under your brand.</p>
            </FadeIn>
          </div>
        </section>

        <section className="py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>The Lab Partnership</SectionTag>
            <FadeIn>
              <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl max-w-2xl leading-[1.02]">
                Everything a lab needs <span className="text-neon">from a planning partner.</span>
              </h2>
            </FadeIn>
            <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {OFFERS.map((o, i) => (
                <FadeIn key={o.t} delay={i * 0.06} className="h-full">
                  <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-7 lg:p-8 transition-colors duration-500 hover:border-neon/40" data-testid={`labs-offer-${i}`}>
                    <o.icon className="h-7 w-7 text-neon mb-5" />
                    <p className="font-display font-bold text-lg">{o.t}</p>
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">{o.d}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <FadeIn>
              <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl">Ready to scale your aligner line <span className="text-neon">without hiring planners?</span></h2>
            </FadeIn>
            <FadeIn delay={0.15}>
              <Link to="/contact" data-testid="labs-cta"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_50px_rgba(0,229,255,0.5)]">
                Start the conversation <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
        </section>
      </motion.div>
    </MarketingLayout>
  );
}
